import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {CodaltComponent} from '../codalt/codalt.component';
import {Ttm} from '../../classes/ttm';
import {Workorder} from '../../classes/workorder';
import {ComponentChanges} from '../../ComponentChanges';
import {TtmType} from '../../classes/ttmtype';
import {TtmStatus} from '../../classes/ttmstatus';
import * as turf from '@turf/turf';
import {Position} from '@turf/helpers';
import {Project} from '../../classes/project';
import {WorkorderService} from '../../services/workorder.service';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {ActivatedRoute} from '@angular/router';

@Component({
    selector: 'app-workorder-compose',
    templateUrl: './workorder-compose.component.html',
    styleUrl: './workorder-compose.component.scss'
})
export class WorkorderComposeComponent extends CodaltComponent implements OnInit, OnChanges {

    @Input() project: Project;
    @Input() selectionPolygon: { lat: number, lng: number }[];
    @Input() filteredTtms: Ttm[];
    @Input() ttmTypes: TtmType[];
    @Input() ttmStatuses: TtmStatus[];

    @Output() toggleSelection = new EventEmitter<void>();
    @Output() updatedWorkorder = new EventEmitter<void>();
    @Output() selectedWorkorder = new EventEmitter<Workorder>();
    @Input() mapSelectionEnabled = false;

    workorder: Workorder;
    includedTtms: Ttm[];
    toAddTtms: Ttm[];
    toRemoveTtms: Ttm[];

    constructor(private workorderService: WorkorderService,
                private confirmDialog: ConfirmDialogService,
                private activeRoute: ActivatedRoute,
                private cdref: ChangeDetectorRef) {
        super();
    }

    ngOnInit() {
        const queryParams = this.activeRoute.snapshot.queryParams as { workorder: number };
        if (queryParams.workorder) {
            this.workorderChange(+queryParams.workorder);
        }
    }

    ngOnChanges(changes: ComponentChanges<WorkorderComposeComponent>) {
        if(this.includedTtms) {
            this.includedTtms = [...this.includedTtms];
        }
        this.setToAddTtms(this.filteredTtms ?? []);
        this.setIncludesTtms();
    }

    workorderChange(workorderId: number) {
        this.subscriptions.add(this.workorderService.getWorkorder(workorderId).subscribe(wo => {
            this.workorder = wo.data;
            this.selectedWorkorder.emit(this.workorder);
            this.setIncludesTtms(true);
            this.setToAddTtms(this.filteredTtms ?? []);
            this.cdref.detectChanges();
        }));
    }

    setIncludesTtms(clear = false) {
        if (clear) {
            this.includedTtms = null;
        }
        if (!this.includedTtms && this.project?.ttms && this.workorder) {
            this.includedTtms = this.project?.ttms?.filter(ttm => this.workorder?.workorders_has_ttms.find(woht => woht.ttm_id === ttm.id)) ?? [];
        }
    }

    setToAddTtms(ttms: Ttm[]) {
        const currentTtmIds = this.includedTtms?.map(ttm => ttm.id) ?? [];
        let filteredTtms = ttms?.filter(ttm => !currentTtmIds.includes(ttm.id)) ?? [];
        if (this.mapSelectionEnabled && this.selectionPolygon?.length > 2) {
            filteredTtms = filteredTtms.filter(ttm => {
                return turf.booleanPointInPolygon(
                    turf.point(ttm.entities[0].gnd.coordinates[0] as Position),
                    turf.polygon([[...this.selectionPolygon.map(sp => {
                        return [sp.lng, sp.lat];
                    }), [this.selectionPolygon[0].lng, this.selectionPolygon[0].lat]]] as any)
                )
            });
        }
        this.toRemoveTtms = ttms.filter(ttm => {
            return currentTtmIds.includes(ttm.id) && (!this.selectionPolygon || (this.selectionPolygon?.length > 2 && turf.booleanPointInPolygon(
                    turf.point(ttm.entities[0].gnd.coordinates[0] as Position),
                    turf.polygon([[...this.selectionPolygon.map(sp => {
                        return [sp.lng, sp.lat];
                    }), [this.selectionPolygon[0].lng, this.selectionPolygon[0].lat]]] as any))
            ))
        });
        this.toAddTtms = filteredTtms;
    }

    addTtms() {
        this.includedTtms = [...this.includedTtms, ...this.toAddTtms];
        this.setToAddTtms(this.filteredTtms);
        this.saveLinkedTtms();
        if (this.mapSelectionEnabled) {
            this.toggleSelection.emit();
        }
    }

    removeSelectedTtms() {
        this.includedTtms = this.includedTtms.filter(ttm => !this.toRemoveTtms.map(t => t.id).includes(ttm.id));
        this.setToAddTtms(this.filteredTtms);
        this.saveLinkedTtms();
        if (this.mapSelectionEnabled) {
            this.toggleSelection.emit();
        }
    }

    removeAllTtms() {
        this.confirmDialog.confirm(
            'Items uit werkopdracht verwijderen',
            `Weet je zeker dat je <b>alle ${this.includedTtms?.length} items</b> uit de werkopdracht ${this.workorder.id_4ps} wilt verwijderen?`,
            'Verwijderen',
            'Behouden'
        ).then(() => {
            this.includedTtms = [];
            this.setToAddTtms(this.filteredTtms);
            this.saveLinkedTtms();
        }, () => {

        });
    }

    private saveLinkedTtms() {
        this.subscriptions.add(this.workorderService.saveTtmsToWorkorder(this.workorder.id, this.includedTtms.map(ttm => ttm.id)).subscribe(ttmIds => {
            this.includedTtms = this.project.ttms.filter(ttm => ttmIds.data.includes(ttm.id));
            this.project.workorders.find(wo => wo.id === this.workorder.id).workorders_has_ttms = ttmIds.data.map(ttmId => {
                return {
                    ttm_id: ttmId,
                    workorder_id: this.workorder.id
                };
            });
            this.updatedWorkorder.emit();
        }));
    }
}
